import { defineStore } from "pinia";
import { ref, watch } from "vue";

import type { PartnerDirectoryRecordSort, PartnerDirectoryRecordStatusFilter } from "@/app/services/partnerDirectoryApi.service";

export const PARTNER_DIRECTORY_FILTERS_STORAGE_KEY = "partner-directory:index-filters";

/** Index-tab sidebar resize bounds; null width means the default `w-80` (320px). */
export const PARTNER_DIRECTORY_INDEX_SIDEBAR_DEFAULT_WIDTH = 320;
export const PARTNER_DIRECTORY_INDEX_SIDEBAR_MIN_WIDTH = 280;
export const PARTNER_DIRECTORY_INDEX_SIDEBAR_MAX_WIDTH = 640;

/** Defaults the "Filters" drawer compares against when counting active filters. */
export const PARTNER_DIRECTORY_DEFAULT_RECORD_STATUS_FILTER: PartnerDirectoryRecordStatusFilter = "screening";
export const PARTNER_DIRECTORY_DEFAULT_RECORD_SORT: PartnerDirectoryRecordSort = "newest";
export const PARTNER_DIRECTORY_DEFAULT_MIN_RATING = "0";
export const PARTNER_DIRECTORY_DEFAULT_MIN_REVIEWS = "0";
export const PARTNER_DIRECTORY_DEFAULT_LIST_LIMIT = "25";

const RECORD_STATUS_FILTERS: PartnerDirectoryRecordStatusFilter[] = ["all", "screening", "new", "needs_review", "approved", "rejected", "imported"];
const RECORD_SORTS: PartnerDirectoryRecordSort[] = ["newest", "recently_edited", "score", "rating", "reviews"];
const MIN_RATINGS = ["0", "3", "4", "4.5"];
const MIN_REVIEWS = ["0", "1", "10", "50"];
const LIST_LIMITS = ["10", "25", "50", "100", "250", "500", "1000"];

interface StoredPartnerDirectoryFilters {
  recordStatusFilter?: PartnerDirectoryRecordStatusFilter;
  recordCountryFilter?: string[];
  recordMinRating?: string;
  recordMinReviews?: string;
  recordSort?: PartnerDirectoryRecordSort;
  importedRecordsLimit?: string;
  indexSidebarWidth?: number | null;
}

function isIncluded<T extends string>(values: readonly T[], value: unknown): value is T {
  return typeof value === "string" && values.includes(value as T);
}

function clampIndexSidebarWidth(width: number): number {
  return Math.min(PARTNER_DIRECTORY_INDEX_SIDEBAR_MAX_WIDTH, Math.max(PARTNER_DIRECTORY_INDEX_SIDEBAR_MIN_WIDTH, Math.round(width)));
}

function readStoredFilters(): StoredPartnerDirectoryFilters | null {
  if (typeof window === "undefined") return null;

  try {
    const rawValue = window.localStorage.getItem(PARTNER_DIRECTORY_FILTERS_STORAGE_KEY);
    if (!rawValue) return null;

    const value = JSON.parse(rawValue) as Record<string, unknown>;
    if (!value || typeof value !== "object" || Array.isArray(value)) return null;

    return {
      recordStatusFilter: isIncluded(RECORD_STATUS_FILTERS, value.recordStatusFilter) ? value.recordStatusFilter : undefined,
      recordCountryFilter: Array.isArray(value.recordCountryFilter)
        ? [...new Set(value.recordCountryFilter.filter((country): country is string => typeof country === "string"))]
        : undefined,
      recordMinRating: isIncluded(MIN_RATINGS, value.recordMinRating) ? value.recordMinRating : undefined,
      recordMinReviews: isIncluded(MIN_REVIEWS, value.recordMinReviews) ? value.recordMinReviews : undefined,
      recordSort: isIncluded(RECORD_SORTS, value.recordSort) ? value.recordSort : undefined,
      importedRecordsLimit: isIncluded(LIST_LIMITS, value.importedRecordsLimit) ? value.importedRecordsLimit : undefined,
      indexSidebarWidth: typeof value.indexSidebarWidth === "number" && Number.isFinite(value.indexSidebarWidth) ? clampIndexSidebarWidth(value.indexSidebarWidth) : undefined,
    };
  } catch {
    return null;
  }
}

export const usePartnerDirectoryFiltersStore = defineStore("partnerDirectoryFilters", () => {
  const storedFilters = readStoredFilters();
  const hasStoredCountryFilter = ref(storedFilters?.recordCountryFilter !== undefined);
  const recordStatusFilter = ref<PartnerDirectoryRecordStatusFilter>(storedFilters?.recordStatusFilter ?? PARTNER_DIRECTORY_DEFAULT_RECORD_STATUS_FILTER);
  const recordCountryFilter = ref<string[]>(storedFilters?.recordCountryFilter ?? []);
  const recordMinRating = ref(storedFilters?.recordMinRating ?? PARTNER_DIRECTORY_DEFAULT_MIN_RATING);
  const recordMinReviews = ref(storedFilters?.recordMinReviews ?? PARTNER_DIRECTORY_DEFAULT_MIN_REVIEWS);
  const recordSort = ref<PartnerDirectoryRecordSort>(storedFilters?.recordSort ?? PARTNER_DIRECTORY_DEFAULT_RECORD_SORT);
  const importedRecordsLimit = ref(storedFilters?.importedRecordsLimit ?? PARTNER_DIRECTORY_DEFAULT_LIST_LIMIT);
  const indexSidebarWidth = ref<number | null>(storedFilters?.indexSidebarWidth ?? null);

  function setRecordCountryFilter(countries: string[]) {
    recordCountryFilter.value = [...new Set(countries)];
  }

  function setIndexSidebarWidth(width: number | null) {
    indexSidebarWidth.value = width === null ? null : clampIndexSidebarWidth(width);
  }

  function persistFilters() {
    if (typeof window === "undefined") return;

    try {
      window.localStorage.setItem(
        PARTNER_DIRECTORY_FILTERS_STORAGE_KEY,
        JSON.stringify({
          recordStatusFilter: recordStatusFilter.value,
          recordCountryFilter: recordCountryFilter.value,
          recordMinRating: recordMinRating.value,
          recordMinReviews: recordMinReviews.value,
          recordSort: recordSort.value,
          importedRecordsLimit: importedRecordsLimit.value,
          indexSidebarWidth: indexSidebarWidth.value,
        } satisfies StoredPartnerDirectoryFilters),
      );
    } catch {
      // Storage can be unavailable in private browsing or locked-down contexts.
    }
  }

  watch(
    [recordStatusFilter, recordCountryFilter, recordMinRating, recordMinReviews, recordSort, importedRecordsLimit, indexSidebarWidth],
    persistFilters,
    { deep: true },
  );

  return {
    hasStoredCountryFilter,
    importedRecordsLimit,
    indexSidebarWidth,
    recordCountryFilter,
    recordMinRating,
    recordMinReviews,
    recordSort,
    recordStatusFilter,
    setIndexSidebarWidth,
    setRecordCountryFilter,
  };
});
